// Loyalty-domain formatters. Kept locale-agnostic (en-US) to match the mockup's
// `fmtN` / `fmt$` helpers; revisit if/when the host app adopts i18n.

const numberFormatter = new Intl.NumberFormat('en-US')
const moneyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

export const formatPoints = (value: number | null | undefined): string => {
  if (value === null || value === undefined || Number.isNaN(value)) return '0'
  return numberFormatter.format(value)
}

// Default redemption rate when LoyaltyRules haven't loaded yet, matching the
// mockup's `getRedemptionRate()` fallback (20 pts = $1).
export const REDEMPTION_RATE_FALLBACK = 20

// LoyaltyRules.redemption.rate is stored as a string in "pts = $1" units (e.g.
// "20" → 20 points are worth $1). Coerce it to a positive number, falling back
// to the mockup default when it's missing or invalid.
export const resolveRedemptionRate = (
  rate: string | number | null | undefined,
): number => {
  const parsed = typeof rate === 'string' ? Number.parseFloat(rate) : rate
  return Number.isFinite(parsed) && (parsed as number) > 0
    ? (parsed as number)
    : REDEMPTION_RATE_FALLBACK
}

// Dollar value of a points balance at the store's redemption rate. `rate` is in
// "pts = $1" units (LoyaltyRules.redemption.rate), so dollars = points / rate —
// the same formula the mockup uses for its "≈ $X value" sub-text. Callers pass
// the live rate from LoyaltyContext; omit it only when rules are unavailable.
export const formatPointsAsDollars = (
  points: number | null | undefined,
  rate?: string | number | null,
): string => {
  const safe =
    points === null || points === undefined || Number.isNaN(points) ? 0 : points
  return moneyFormatter.format(safe / resolveRedemptionRate(rate))
}

// For values the API already gives in dollars (e.g. LoyaltyOverviewSummary.pointsInValue).
export const formatDollars = (amount: number | null | undefined): string => {
  const safe =
    amount === null || amount === undefined || Number.isNaN(amount) ? 0 : amount
  return moneyFormatter.format(safe)
}

export const formatSignedPoints = (value: number): string => {
  if (value > 0) return `+${numberFormatter.format(value)}`
  if (value < 0) return numberFormatter.format(value)
  return '0'
}

const monthDayYearFormatter = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
})

export const formatLastActivity = (iso: string | null | undefined): string => {
  if (!iso) return 'Never'
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return '—'
  return monthDayYearFormatter.format(date)
}

const shortMonthFormatter = new Intl.DateTimeFormat('en-US', { month: 'short' })

// Server returns 1-based months (1 = Jan). Build a date at the first of that
// month to format. Year is currently unused in labels (mockup shows month only)
// but accepted so we can extend to "Jan '24" without API churn.
export const formatChartMonthLabel = (year: number, month: number): string => {
  if (!Number.isFinite(year) || !Number.isFinite(month)) return ''
  const safeMonth = Math.max(1, Math.min(12, Math.round(month)))
  return shortMonthFormatter.format(new Date(year, safeMonth - 1, 1))
}

export const formatPercentSigned = (pct: number): string => {
  if (!Number.isFinite(pct) || pct === 0) return '0%'
  const rounded = Math.round(pct)
  return `${rounded > 0 ? '↑' : '↓'} ${Math.abs(rounded)}%`
}
